// Display-only line-item card. Production status + tracking are edited at the
// ORDER level (see OrderDetails) — per-line-item editing of those was deprecated
// and removed (the backend only exposes order-level production_status/tracking).
//
// Every field below now reads ONE source: the typed value OrderDetails passes
// through from the API. What used to be here instead was a chain of four
// fallbacks per field, and the Gender bug showed what they were really worth:
//
//   currentLineItemAttribbutes?.Gender      <- always undefined. It came from
//        `metafield_attributes`, a key no endpoint in this system has ever
//        produced and which is not a Shopify order field either.
//   dbObject[itemVal.id]["Gender"]          <- always undefined. OrderDBData rows
//        carry ItemID/EnterpriseName/TeamName/PlayerID/PlayerName/
//        Tag/PID/RepeatedPlayer. There is no Gender key and never was.
//   getValueByName("ShoeSizeGender", ...)   <- the typo'd one, which returned a
//        TRUTHY "-" on a miss and so also blocked the line below it.
//   itemVal?.db_sex                         <- the only live source. Unreachable.
//
// Four apparent safety nets; two long dead, one broken, one unreachable behind
// it. A field with one honest source is more robust than a field with four
// dishonest ones, because you can see when it is empty.
import { useState } from "react";
import toast from "react-hot-toast";
import { CButton, CFormSelect, CLoadingButton, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle } from "@coreui/react-pro";
import { authFetch } from "../utils/authFetch";
import Constants from "../Constants";

const ADMIN_ENDPOINT = Constants.AUTH_BASE_URL + "/api/v1/admin";

// Half sizes 3–24, the same range the App Clip accepts.
const SHOE_SIZES = Array.from({ length: 43 }, (_, k) => (3 + k * 0.5).toString());

// Support's edit of a placed order's pair: size, gender, nickname, shoe type,
// width. Lhianie (28 Oct 2026): these used to be edited in a Shopify metafield
// that prodmake rendered; nothing in the new system read that, so she had no way
// to fix a wrong size. Saves to our row (PATCH /admin/orders/:slug/line_items/:slug),
// which is the only writer of pair data; Shopify gets a one-way mirror.
//
// Not here, on purpose: the MODEL and the quantity. They are the Shopify line the
// customer paid for — the receipt — and changing them after payment is a Shopify
// order edit or a refund and reorder. The factory's status is the lock: the button
// only shows while the order is Awaiting Review. A reason is required and logged.
// A shoe-specific pair keeps its shoe's size.
export default function OrderSingle({ itemVal, i, lang, OrderDBData, orderSlug, editable, onSaved }) {
  const [edit, setEdit] = useState({ visible: false, saving: false, form: null });
  const openEdit = () =>
    setEdit({
      visible: true,
      saving: false,
      form: {
        reason: "",
        shoe_size: itemVal?.shoe_size != null ? String(itemVal.shoe_size) : "",
        shoe_size_gender: itemVal?.shoe_size_gender || "",
        nickname: itemVal?.nickname || "",
      },
    });
  const setField = (key, value) => setEdit((e) => ({ ...e, form: { ...e.form, [key]: value } }));
  const saveEdit = async () => {
    setEdit((e) => ({ ...e, saving: true }));
    try {
      if (!edit.form.reason.trim()) {
        toast.error(lang === "en" ? "Please say why (e.g. customer emailed asking for size 11)." : "请填写原因");
        setEdit((e) => ({ ...e, saving: false }));
        return;
      }
      const body = {
        reason: edit.form.reason.trim(),
        nickname: edit.form.nickname,
      };
      if (!itemVal?.shoe_specific) {
        body.shoe_size = edit.form.shoe_size;
        body.shoe_size_gender = edit.form.shoe_size_gender;
      }
      const response = await authFetch(`${ADMIN_ENDPOINT}/orders/${orderSlug}/line_items/${itemVal.slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Could not save the pair");
      toast.success(data.message || "Pair updated");
      setEdit({ visible: false, saving: false, form: null });
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.message || "Could not save the pair");
      setEdit((e) => ({ ...e, saving: false }));
    }
  };

  const dbObject = OrderDBData.reduce((acc, item) => {
    acc[item.ItemID] = item;
    return acc;
  }, {});

  // Keyed by ItemID like every other lookup in this component. It used to be
  // OrderDBData[i] — a positional index into the same array the line beside it
  // keys by id, which silently mismatches the moment the two orders diverge.
  const dbRow = dbObject?.[itemVal?.id];

  const errorIndicator = () => {
    if (itemVal?.footScans?.length === 0) {
      return <span className="p-2 test-tag-highlight">{`(Error)`}</span>;
    }
    if (itemVal?.shoe_specific && itemVal?.insoleScans?.length === 0) {
      return <span className="p-2 test-tag-highlight">{`(Error)`}</span>;
    }
    return <></>;
  };

  return (
    <div className="row" key={`orderdetails${i}`}>
      <div className="col-md-12">
        <div className="card shadow mb-4">
          <div className="card-body item-card">
            <div className="row">
              <div className="col-md-12">
                <div className="d-flex justify-content-between mb-3">
                  <h5 className="order-details-title">
                    {lang === "en" ? "Item Details" : "项目明细"}
                    {errorIndicator()}
                  </h5>
                  {editable && itemVal?.slug ? (
                    <CButton size="sm" color="dark" variant="outline" title={lang === "en" ? "Edit pair" : "编辑"} onClick={openEdit}>
                      {lang === "en" ? "Edit pair" : "编辑"}
                    </CButton>
                  ) : null}
                </div>

                <div className="row justify-content-between">
                  <div className="col-md-2 groov-item-image">
                    <img
                      src={itemVal.image}
                      alt="insole-full"
                      className="img-fluid"
                    />
                  </div>
                  <div className="col-md-4 ellipsis">
                    <h5 className="order-details-title">
                      {lang === "en" ? "Configuration" : "配置"}
                    </h5>
                    <table
                      className="table table-borderless"
                      key={`configuration${i}`}
                    >
                      <tbody>
                        <tr>
                          <td className="">
                            {lang === "en" ? "Nickname" : "昵称"}
                          </td>
                          <td>{itemVal?.nickname || "-"}</td>
                        </tr>

                        <tr>
                          <td>{lang === "en" ? "Quantity" : "数量"}</td>
                          <td>{itemVal.quantity}</td>
                        </tr>
                        <tr>
                          <td>{"Type Of Finish"}</td>
                          <td>
                           {itemVal?.finish || "-"}
                          </td>
                        </tr>
                        {/* Per PAIR, which is the level the trainer actually
                            requested them at. The summary above the Note is a
                            convenience for reading the whole order at a glance;
                            THIS is where the maker of this specific insole
                            reads what to do differently. */}
                        {itemVal?.special_modifications && (
                          <tr>
                            <td>{"Special Modifications"}</td>
                            <td className="modifications-cell-highlight">
                              {itemVal.special_modifications}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>{"Groov Shape"}</td>
                          <td>
                            {itemVal?.shoe_specific ? "Shoe Specific" : "General Purpose"}
                          </td>
                        </tr>
                        <tr>
                          {/* The kind of shoe the insole is for ("Work Boots",
                              "Athletic", ...). The API has sent `shoe_type` on
                              every line item since the start; this row is the
                              first place that shows it. The old factory console
                              had a dedicated field for it, and the factory reads
                              it when choosing the blank. */}
                          <td>{lang === "en" ? "Shoe Type" : "鞋类"}</td>
                          <td>{itemVal?.shoe_type || "-"}</td>
                        </tr>
                        {itemVal?.shoe_specific && (
                          <tr>
                            {/* Was `itemVal?.shoeNickname` — an identifier this
                                file only ever READ; nothing has ever written it,
                                so this row was blank on every shoe-specific
                                order. The shoe's name is `shoe_name`. */}
                            <td>{"Shoe Nickname"}</td>
                            <td>{itemVal?.shoe_name || "-"}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-3">
                    <h5 className="order-details-title">
                      {lang === "en" ? "Personal Details" : "个人信息"}

                      <span className="badgeForPlayer">
                        {dbRow?.RepeatedPlayer ? "Repeated " : "New"}
                      </span>
                    </h5>
                    <table
                      className="table table-borderless"
                      key={`personal${i}`}
                    >
                      <tbody>
                        <tr>
                          <td className={!dbRow?.PlayerName ? "missing-field-highlight" : ""}>
                            {lang === "en" ? "Player Name" : "球员姓名"}
                          </td>
                          <td>{dbRow?.PlayerName || "-"}</td>
                        </tr>
                        <tr>
                          <td>{lang === "en" ? "Team Name" : "球队名称"}</td>
                          <td>{dbRow?.TeamName || "-"}</td>
                        </tr>
                        {/* Migrated pairs only: what the old console showed the
                            factory, as typed in the old app. Orders placed here
                            never carry these, so the rows do not appear. */}
                        {itemVal?.player_height && (
                          <tr>
                            <td>{lang === "en" ? "Height" : "身高"}</td>
                            <td>{itemVal.player_height}</td>
                          </tr>
                        )}
                        {itemVal?.player_weight && (
                          <tr>
                            <td>{lang === "en" ? "Weight" : "体重"}</td>
                            <td>{itemVal.player_weight} lbs</td>
                          </tr>
                        )}
                        {itemVal?.player_age && (
                          <tr>
                            <td>{lang === "en" ? "Age" : "年龄"}</td>
                            <td>{itemVal.player_age} yrs</td>
                          </tr>
                        )}
                        {itemVal?.pain_area && (
                          <tr>
                            <td>{lang === "en" ? "Pain Area" : "疼痛部位"}</td>
                            <td>{itemVal.pain_area}</td>
                          </tr>
                        )}
                        <tr>
                          {/* The highlight now tests the SAME single expression
                              the cell renders, so it cannot disagree with what is
                              on screen. It used to test a four-term chain whose
                              third term returned a truthy "-" on a miss, which
                              made `!term` false and silently disarmed the warning
                              on the one field that most needed it: a men's 9 and a
                              women's 9 are the same number and a different last. */}
                          <td className={!itemVal?.shoe_size_gender ? "missing-field-highlight" : ""}>
                            {lang === "en" ? "Gender" : "性别"}
                          </td>
                          <td>{itemVal?.shoe_size_gender || "N/A"}</td>
                        </tr>

                        <tr>
                          <td className={!itemVal?.shoe_size ? "missing-field-highlight" : ""}>
                            {lang === "en" ? "Shoe Size" : "鞋码"}
                          </td>
                          <td>{itemVal?.shoe_size || "-"}</td>
                        </tr>

                        <tr>
                          <td>{lang === "en" ? "Non Standard Width" : "非标准宽度"}</td>
                          <td>{itemVal?.non_standard_width || "-"}</td>
                        </tr>
                        {/* An "Aditional Details" row used to sit here. Nothing in
                            this system has ever produced such a property, so it
                            rendered "-" on every line item that has ever existed —
                            a column of dashes next to the fields that DO carry
                            manufacturing data. Removed rather than left to look
                            like a value that simply happened to be empty. */}
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-3">
                    <h5 className="order-details-title ">
                      {lang === "en" ? "Foot Scans" : "足部掃描"}
                    </h5>

                    {(itemVal.footScans || []).length === 0 && itemVal.footScanNotMigrated && (
                      <span className="missing-field-highlight scan-missing">
                        {lang === "en"
                          ? `Foot scan #${itemVal.footScanNotMigrated} is not in this system. It belongs to an old-system player who had no email address (or was deleted there), so no account could be created for them and their scans were not migrated. This order is attached to the account that placed it. The feet are still in prodmake while it is running.`
                          : `足部扫描 #${itemVal.footScanNotMigrated} 不在本系统中。它属于旧系统中没有电子邮件地址（或已被删除）的球员，因此无法为其创建账户，其扫描也未迁移。请在 prodmake 仍可用时使用它。`}
                      </span>
                    )}

                    {(itemVal.footScans || []).length === 0 && !itemVal.footScanNotMigrated && (
                      <span className="missing-field-highlight scan-missing">
                        {lang === "en" ? "Error: Foot Scans are Missing" : "错误：缺少足部扫描"}
                      </span>
                    )}

                    {(itemVal.footScans || []).length > 0 && (
                      <ul className="scans-list">
                        {itemVal.footScans.map((scan, index) => (
                          <li key={index} style={{ marginBottom: "6px" }}>
                            <div><strong>{scan.label}</strong></div>
                            <div className="d-flex gap-2">
                              <a
                                href={scan.ply_url || scan.url}
                                download={`${scan.label}.ply`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none", color: "inherit" }}
                              >
                                <i className="fas fa-download fa-sm"></i>
                                <span> PLY</span>
                              </a>
                              {scan.glb_url && (
                                <a
                                  href={scan.glb_url}
                                  download={`${scan.label}.glb`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: "none", color: "inherit" }}
                                >
                                  <i className="fas fa-download fa-sm"></i>
                                  <span> GLB</span>
                                </a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                </div>
                {itemVal?.shoe_specific && (
                  <div className="row" style={{ marginTop: "15px" }}>
                    <div className="col-md-12">
                      <h5 className="order-details-title">
                        {lang === "en" ? "Insole Scans" : "鞋垫扫描"}
                      </h5>

                      {(itemVal.insoleScans || []).length === 0 && (
                        <span className="missing-field-highlight scan-missing">
                          {lang === "en" ? "Error: Insole Scans are Missing" : "错误：缺少鞋垫扫描"}
                        </span>
                      )}

                      {(itemVal.insoleScans || []).length > 0 && (
                        <ul className="scans-list">
                          {itemVal.insoleScans.map((scan, index) => (
                            <li key={index} style={{ marginBottom: "6px" }}>
                              <div><strong>{scan.label}</strong></div>
                              <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                                {scan.ply_url && (
                                  <a href={scan.ply_url} target="_blank" rel="noopener noreferrer">
                                    <i className="fas fa-download fa-sm"></i> .ply
                                  </a>
                                )}
                                {scan.glb_url && (
                                  <a href={scan.glb_url} target="_blank" rel="noopener noreferrer">
                                    <i className="fas fa-download fa-sm"></i> .glb
                                  </a>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {itemVal.shoe_name && (
                        <p style={{ marginTop: "8px" }}>
                          <strong>{lang === "en" ? "Shoe:" : "鞋："}</strong>{" "}
                          {itemVal.shoe_name}
                          {itemVal.shoe_brand && ` (${itemVal.shoe_brand}${itemVal.shoe_model ? ` ${itemVal.shoe_model}` : ""})`}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CModal alignment="center" visible={edit.visible} onClose={() => setEdit({ visible: false, saving: false, form: null })}>
        <CModalHeader>
          <CModalTitle>{lang === "en" ? "Edit pair" : "编辑"}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {edit.form ? (
            <div style={{ display: "grid", gap: "0.9rem" }}>
              <div className="small text-secondary">
                {lang === "en"
                  ? `Model: ${itemVal?.finish || "—"} (what the customer paid for; change it in the Shopify order, or refund and reorder).`
                  : `型号：${itemVal?.finish || "—"}（客户已付款的商品；如需更改请修改 Shopify 订单或退款重新下单）。`}
              </div>
              <div style={{ display: "flex", gap: "0.9rem" }}>
                <div className="flex-fill">
                  <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }} htmlFor={`pairSize${i}`}>{lang === "en" ? "Shoe size" : "尺码"}</label>
                  <CFormSelect style={{ width: "100%" }} id={`pairSize${i}`} value={edit.form.shoe_size} disabled={!!itemVal?.shoe_specific} onChange={(e) => setField("shoe_size", e.target.value)}>
                    <option value="">—</option>
                    {!SHOE_SIZES.includes(edit.form.shoe_size) && edit.form.shoe_size ? <option value={edit.form.shoe_size}>{edit.form.shoe_size}</option> : null}
                    {SHOE_SIZES.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
                  </CFormSelect>
                </div>
                <div className="flex-fill">
                  <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }} htmlFor={`pairGender${i}`}>{lang === "en" ? "Gender" : "性别"}</label>
                  <CFormSelect style={{ width: "100%" }} id={`pairGender${i}`} value={edit.form.shoe_size_gender} disabled={!!itemVal?.shoe_specific} onChange={(e) => setField("shoe_size_gender", e.target.value)}>
                    <option value="">—</option>
                    <option value="male">male</option>
                    <option value="female">female</option>
                  </CFormSelect>
                </div>
              </div>
              {itemVal?.shoe_specific ? (
                <div className="small text-secondary">
                  {lang === "en"
                    ? `Size and gender follow the shoe "${itemVal?.shoe_name || ""}"; edit the shoe in the locker to change them.`
                    : `尺码和性别跟随鞋子 "${itemVal?.shoe_name || ""}"；请在鞋柜中编辑鞋子。`}
                </div>
              ) : null}
              <div>
                <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }} htmlFor={`pairNickname${i}`}>{lang === "en" ? "Nickname (engraved, max 15)" : "昵称（刻字，最多 15 字符）"}</label>
                <input id={`pairNickname${i}`} className="form-control" maxLength={15} value={edit.form.nickname} onChange={(e) => setField("nickname", e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: "0.9rem" }}>
                <div className="flex-fill">
                  <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }}>{lang === "en" ? "Shoe type" : "鞋类"}</label>
                  <input className="form-control" value={itemVal?.shoe_type || "—"} disabled readOnly title={lang === "en" ? "Set when the order was placed; not editable." : "下单时设定，不可编辑。"} />
                </div>
                <div className="flex-fill">
                  <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }}>{lang === "en" ? "Non-standard width" : "非标准宽度"}</label>
                  <input className="form-control" value={itemVal?.non_standard_width || "—"} disabled readOnly title={lang === "en" ? "Set when the order was placed; not editable." : "下单时设定，不可编辑。"} />
                </div>
              </div>
              <div>
                <label className="form-label" style={{ display: "block", marginBottom: "0.25rem" }} htmlFor={`pairReason${i}`}>{lang === "en" ? "Reason (required, kept in the log)" : "原因（必填，记录在日志）"}</label>
                <input id={`pairReason${i}`} className="form-control" value={edit.form.reason} onChange={(e) => setField("reason", e.target.value)} placeholder={lang === "en" ? "e.g. customer emailed 29 Oct asking for size 11" : "例如：客户来信要求 11 码"} />
              </div>
              <div className="small text-secondary">
                {lang === "en"
                  ? "Saved here and shown to the factory immediately. Editing is only possible while the order is Awaiting Review."
                  : "在此保存后工厂立即可见。仅在“等待审核”状态下可编辑。"}
              </div>
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setEdit({ visible: false, saving: false, form: null })}>
            {lang === "en" ? "Cancel" : "取消"}
          </CButton>
          {edit.saving ? (
            <CLoadingButton color="primary">{lang === "en" ? "Saving…" : "保存中…"}</CLoadingButton>
          ) : (
            <CButton color="primary" onClick={saveEdit}>{lang === "en" ? "Save pair" : "保存"}</CButton>
          )}
        </CModalFooter>
      </CModal>
    </div>
  );
}
